import { decAdd, decCmp, decSub, formatDec, isDecimalText } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  FileButton,
  MultiSelect,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowsLeftRight,
  IconDownload,
  IconFileText,
  IconInfoCircle,
  IconLogout,
  IconPaperclip,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortPicker } from '../../inputs/PortPicker.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { DateTime, EMPTY_CAN } from './BunkeringPanel.js';
import classes from './Ledger.module.css';
import { useLedgerRecords } from './ledger-state.js';
import {
  DecisionModal,
  joinLocal,
  mt,
  Notices,
  OfficeNote,
  orNull,
  RecordPicker,
  StatusBadge,
  StatusBanner,
  splitLocal,
  WorkflowButtons,
  workflowActions,
} from './ledger-ui.js';
import type { FuelSource } from './source.js';
import type { FuelContextData, FuelOperationKind } from './types.js';

const KINDS: readonly { kind: FuelOperationKind; icon: ReactNode }[] = [
  { kind: 'transfer', icon: <IconArrowsLeftRight size={22} /> },
  { kind: 'discharge', icon: <IconDownload size={22} /> },
  { kind: 'return', icon: <IconArrowBackUp size={22} /> },
  { kind: 'fuel_change', icon: <IconRefresh size={22} /> },
  { kind: 'other_out', icon: <IconLogout size={22} /> },
];
const METHODS = ['tank_gauging', 'flowmeter', 'bunker_meter', 'estimate'] as const;
const DESCRIPTION_MAX = 500;

export interface OperationValues {
  kind: FuelOperationKind;
  startDay: string | null;
  startTime: string | null;
  endDay: string | null;
  endTime: string | null;
  sourceTankId: string | null;
  targetTankId: string | null;
  fuelTypeId: string | null;
  massMt: string;
  measurementMethod: string | null;
  responsibleId: string | null;
  receiver: string;
  portName: string;
  portCode: string | null;
  bunkeringId: string | null;
  reason: string;
  equipmentId: string | null;
  previousFuelTypeId: string | null;
  newFuelTypeId: string | null;
  sourceTankIds: string[];
  description: string;
}

export const EMPTY_OPERATION: OperationValues = {
  kind: 'transfer',
  startDay: null,
  startTime: null,
  endDay: null,
  endTime: null,
  sourceTankId: null,
  targetTankId: null,
  fuelTypeId: null,
  massMt: '',
  measurementMethod: 'tank_gauging',
  responsibleId: null,
  receiver: '',
  portName: '',
  portCode: null,
  bunkeringId: null,
  reason: '',
  equipmentId: null,
  previousFuelTypeId: null,
  newFuelTypeId: null,
  sourceTankIds: [],
  description: '',
};

const str = (value: unknown) => (typeof value === 'string' ? value : '');
const opt = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);

export function operationValuesOf(input: Record<string, unknown>): OperationValues {
  const start = splitLocal(input.startedLocal);
  const end = splitLocal(input.endedLocal);
  return {
    kind: (opt(input.kind) ?? 'transfer') as FuelOperationKind,
    startDay: start.day,
    startTime: start.time,
    endDay: end.day,
    endTime: end.time,
    sourceTankId: opt(input.sourceTankId),
    targetTankId: opt(input.targetTankId),
    fuelTypeId: opt(input.fuelTypeId),
    massMt: str(input.massMt),
    measurementMethod: opt(input.measurementMethod),
    responsibleId: opt(input.responsibleId),
    receiver: str(input.receiver),
    portName: str(input.portName),
    portCode: opt(input.portCode),
    bunkeringId: opt(input.bunkeringId),
    reason: str(input.reason),
    equipmentId: opt(input.equipmentId),
    previousFuelTypeId: opt(input.previousFuelTypeId),
    newFuelTypeId: opt(input.newFuelTypeId),
    sourceTankIds: Array.isArray(input.sourceTankIds) ? input.sourceTankIds.map(String) : [],
    description: str(input.description),
  };
}

export function operationInputOf(v: OperationValues): Record<string, unknown> {
  return {
    kind: v.kind,
    startedLocal: joinLocal(v.startDay, v.startTime),
    endedLocal: joinLocal(v.endDay, v.endTime),
    sourceTankId: v.sourceTankId,
    targetTankId: v.targetTankId,
    fuelTypeId: v.fuelTypeId,
    massMt: orNull(v.massMt),
    measurementMethod: v.measurementMethod,
    responsibleId: v.responsibleId,
    receiver: orNull(v.receiver),
    portName: orNull(v.portName),
    portCode: v.portCode,
    bunkeringId: v.bunkeringId,
    reason: orNull(v.reason),
    equipmentId: v.equipmentId,
    previousFuelTypeId: v.previousFuelTypeId,
    newFuelTypeId: v.newFuelTypeId,
    sourceTankIds: v.sourceTankIds,
    description: orNull(v.description),
  };
}

export interface TransfersPanelProps {
  source: FuelSource;
  vesselId: string | null;
  onVessel?: (data: FuelContextData) => void;
}

/**
 * Transferler sekmesi (ortağın 04-transferler.jpeg tasarımı; ortak belgesi §8): Tank Transferi,
 * Boşaltma, İade, Yakıt Değişimi ve Diğer Çıkış. Transferde kaynak ve hedef tankın önce/sonra
 * şeması ve Ölçüm Kontrolü (defterden); gemi toplam ROB'u değişmez. Onay iki tarafı tek işlemle
 * yazar; yakıt değişimi stok düşürmez. Ofiste salt okunur.
 */
export function TransfersPanel({ source, vesselId, onVessel }: TransfersPanelProps) {
  const { t } = useTranslation();
  const ledger = useLedgerRecords(source, vesselId, 'operation', onVessel);
  const { context, detail } = ledger;
  const [values, setValues] = useState<OperationValues>(EMPTY_OPERATION);
  const [modal, setModal] = useState<'approve' | 'return' | 'reverse' | null>(null);

  useEffect(() => {
    setValues(detail ? operationValuesOf(detail.input) : EMPTY_OPERATION);
  }, [detail]);

  if (ledger.state === 'loading') {
    return <TableSkeleton columns={3} rows={8} label={t('fuel.tabs.transfers')} />;
  }
  if (ledger.state === 'error') {
    return (
      <LoadError
        code={ledger.loadCode ?? 'GENERIC'}
        onRetry={() => void ledger.reload()}
        title={t('fuel.tabs.transfers')}
      />
    );
  }

  const editorSide = source.ledgerEditor !== undefined;
  const status = ledger.selectedId === null ? 'new' : (detail?.status ?? 'draft');
  const editable = editorSide && (status === 'new' || status === 'draft' || status === 'returned');
  const set = <K extends keyof OperationValues>(key: K, value: OperationValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));
  const issue = (path: string) => {
    const code = ledger.fieldIssues[path];
    return code ? t(`fuel.fieldErrors.${code}`, { defaultValue: code }) : null;
  };
  const tanks = context?.tanks ?? [];
  const tankOptions = tanks.map((tank) => ({
    value: tank.id,
    label: `${tank.name} (${tank.code})`,
  }));
  const fuelOptions = (context?.fuelTypes ?? []).map((f) => ({ value: f.id, label: f.code }));
  const crewOptions = (context?.crew ?? []).map((p) => ({
    value: p.id,
    label: p.role ? `${p.name} (${p.role})` : p.name,
  }));
  const kind = values.kind;
  const source1 = tanks.find((x) => x.id === values.sourceTankId) ?? null;
  const target = tanks.find((x) => x.id === values.targetTankId) ?? null;
  const mass = isDecimalText(values.massMt) ? values.massMt : null;

  // Önce/sonra: kayıtlı kayıtta sunucu (bu kayıt hariç defter), yeni ve taslakta istemci önizlemesi
  const preview = (tankId: string | null, sign: 1 | -1) => {
    if (!tankId) return null;
    const saved = detail?.check.find((c) => c.tankId === tankId);
    if (saved && status !== 'new' && detail?.input.massMt === values.massMt) return saved;
    const before = tanks.find((x) => x.id === tankId)?.robMt ?? '0';
    if (!mass) return { tankId, before, after: before };
    return {
      tankId,
      before,
      after: formatDec(sign === 1 ? decAdd(before, mass) : decSub(before, mass)),
    };
  };
  const out = kind === 'fuel_change' ? null : preview(values.sourceTankId, -1);
  const into = kind === 'transfer' ? preview(values.targetTankId, 1) : null;
  const actions = workflowActions(status, context?.can ?? EMPTY_CAN, editorSide);
  const input = () => operationInputOf(values);
  const correctsNo = detail?.correctsId
    ? (ledger.records.find((r) => r.id === detail.correctsId)?.recordNo ?? null)
    : null;
  const tankName = (id: string) => tanks.find((x) => x.id === id)?.name ?? '—';
  const nextNo = detail?.recordNo ?? '—';
  const files = (detail?.documents ?? []).filter((d) => !ledger.removed.includes(d.id));
  const pending = ledger.pendingFiles.other ?? null;

  const time = (
    <>
      <DateTime
        label={t('fuel.transfers.fields.start')}
        day={values.startDay}
        time={values.startTime}
        readOnly={!editable}
        onDay={(v) => set('startDay', v)}
        onTime={(v) => set('startTime', v)}
        error={issue('startedLocal')}
      />
      <DateTime
        label={t('fuel.transfers.fields.end')}
        day={values.endDay}
        time={values.endTime}
        readOnly={!editable}
        onDay={(v) => set('endDay', v)}
        onTime={(v) => set('endTime', v)}
        error={issue('endedLocal')}
      />
    </>
  );
  const tankSelect = (key: 'sourceTankId' | 'targetTankId', label: string) => (
    <Select
      label={label}
      withAsterisk
      data={tankOptions}
      value={values[key]}
      onChange={(value) => set(key, value)}
      readOnly={!editable}
      error={issue(key)}
    />
  );
  const fuelSelect = (
    <Select
      label={t('fuel.transfers.fields.fuel')}
      withAsterisk
      data={fuelOptions}
      value={values.fuelTypeId}
      onChange={(value) => set('fuelTypeId', value)}
      readOnly={!editable}
      error={issue('fuelTypeId')}
    />
  );
  const massInput = (
    <div className={classes.unitInput}>
      <TextInput
        label={t('fuel.transfers.fields.mass')}
        withAsterisk
        inputMode="decimal"
        readOnly={!editable}
        value={values.massMt}
        onChange={(e) => set('massMt', e.currentTarget.value.replace(',', '.'))}
        error={issue('massMt')}
      />
      <span className={classes.unitTag} aria-hidden>
        MT
      </span>
    </div>
  );
  const methodSelect = (
    <Select
      label={t('fuel.transfers.fields.method')}
      withAsterisk={kind === 'transfer'}
      data={METHODS.map((m) => ({ value: m, label: t(`fuel.transfers.methods.${m}`) }))}
      value={values.measurementMethod}
      onChange={(value) => set('measurementMethod', value)}
      readOnly={!editable}
      error={issue('measurementMethod')}
    />
  );
  const responsible = (
    <Select
      label={t('fuel.transfers.fields.responsible')}
      withAsterisk
      data={crewOptions}
      value={values.responsibleId}
      onChange={(value) => set('responsibleId', value)}
      readOnly={!editable}
      searchable
      error={issue('responsibleId')}
    />
  );
  const noField = (
    <TextInput
      label={
        kind === 'transfer'
          ? t('fuel.transfers.fields.recordNo')
          : t('fuel.transfers.fields.movementNo')
      }
      withAsterisk
      readOnly
      className={classes.computed}
      value={status === 'new' ? '' : nextNo}
      placeholder={status === 'new' ? t('fuel.records.new') : undefined}
    />
  );
  const reasonInput = (
    <TextInput
      label={t('fuel.transfers.fields.reason')}
      withAsterisk
      readOnly={!editable}
      value={values.reason}
      onChange={(e) => set('reason', e.currentTarget.value)}
      error={issue('reason')}
    />
  );

  let fields: ReactNode;
  if (kind === 'transfer') {
    fields = (
      <>
        {noField}
        {time}
        {tankSelect('sourceTankId', t('fuel.transfers.fields.sourceTank'))}
        {tankSelect('targetTankId', t('fuel.transfers.fields.targetTank'))}
        {methodSelect}
        {fuelSelect}
        {massInput}
        {responsible}
      </>
    );
  } else if (kind === 'discharge') {
    fields = (
      <>
        {noField}
        {time}
        {tankSelect('sourceTankId', t('fuel.transfers.fields.sourceTank'))}
        <TextInput
          label={t('fuel.transfers.fields.receiver')}
          withAsterisk
          readOnly={!editable}
          value={values.receiver}
          onChange={(e) => set('receiver', e.currentTarget.value)}
          error={issue('receiver')}
        />
        <PortPicker
          label={t('fuel.transfers.fields.port')}
          withAsterisk
          disabled={!editable}
          value={{ name: values.portName, code: values.portCode }}
          onChange={(next) =>
            setValues((prev) => ({ ...prev, portName: next.name, portCode: next.code }))
          }
          error={issue('portName')}
        />
        {fuelSelect}
        {massInput}
        {responsible}
      </>
    );
  } else if (kind === 'return') {
    fields = (
      <>
        {noField}
        {time}
        <Select
          label={t('fuel.transfers.fields.bunkering')}
          withAsterisk
          data={(context?.bunkerings ?? []).map((b) => ({
            value: b.id,
            label: b.bdnNo ? `${b.recordNo} · ${b.bdnNo}` : b.recordNo,
          }))}
          value={values.bunkeringId}
          onChange={(value) => {
            const fuel = context?.bunkerings.find((b) => b.id === value)?.fuelTypeId ?? null;
            setValues((prev) => ({
              ...prev,
              bunkeringId: value,
              fuelTypeId: prev.fuelTypeId ?? fuel,
            }));
          }}
          readOnly={!editable}
          error={issue('bunkeringId')}
        />
        {tankSelect('sourceTankId', t('fuel.transfers.fields.sourceTank'))}
        {reasonInput}
        {fuelSelect}
        {massInput}
        {responsible}
      </>
    );
  } else if (kind === 'fuel_change') {
    fields = (
      <>
        {noField}
        {time}
        <Select
          label={t('fuel.transfers.fields.equipment')}
          withAsterisk
          searchable
          data={(context?.equipment ?? []).map((e) => ({
            value: e.id,
            label: `${e.code} ${e.name}`,
          }))}
          value={values.equipmentId}
          onChange={(value) => set('equipmentId', value)}
          readOnly={!editable}
          error={issue('equipmentId')}
        />
        <Select
          label={t('fuel.transfers.fields.previousFuel')}
          withAsterisk
          data={fuelOptions}
          value={values.previousFuelTypeId}
          onChange={(value) => set('previousFuelTypeId', value)}
          readOnly={!editable}
          error={issue('previousFuelTypeId')}
        />
        <Select
          label={t('fuel.transfers.fields.newFuel')}
          withAsterisk
          data={fuelOptions}
          value={values.newFuelTypeId}
          onChange={(value) => set('newFuelTypeId', value)}
          readOnly={!editable}
          error={issue('newFuelTypeId')}
        />
        <MultiSelect
          label={t('fuel.transfers.fields.sourceTanks')}
          withAsterisk
          data={tankOptions}
          value={values.sourceTankIds}
          onChange={(value) => set('sourceTankIds', value)}
          readOnly={!editable}
          error={issue('sourceTankIds')}
        />
        {responsible}
      </>
    );
  } else {
    fields = (
      <>
        {noField}
        {time}
        {tankSelect('sourceTankId', t('fuel.transfers.fields.sourceTank'))}
        {reasonInput}
        {methodSelect}
        {fuelSelect}
        {massInput}
        {responsible}
      </>
    );
  }

  const tankFigure = (
    tank: (typeof tanks)[number] | null,
    numbers: { before: string; after: string } | null,
  ) => {
    const fill = tank?.fillPct ? Math.max(0, Math.min(100, Number(tank.fillPct))) : 0;
    return (
      <div className={classes.tankSide}>
        <div className={classes.tankShape} aria-hidden>
          <div className={classes.tankFill} style={{ height: `${fill}%` }} />
        </div>
        <div className={classes.tankInfo}>
          <span className={classes.tankName}>{tank?.name ?? '—'}</span>
          <span className={classes.tankCode}>{tank?.code ?? ''}</span>
          {numbers ? (
            <span className={classes.tankNumbers}>
              {t('fuel.transfers.before')}: <strong>{mt(numbers.before)} MT</strong> &nbsp;|&nbsp;{' '}
              {t('fuel.transfers.after')}: <strong>{mt(numbers.after)} MT</strong>
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  const diffText = (before: string, after: string) => {
    const diff = formatDec(decSub(after, before), 2);
    return decCmp(diff, '0') > 0 ? `+${diff}` : diff;
  };

  return (
    <section className={classes.card} aria-label={t(`fuel.transfers.titles.${kind}`)}>
      <div className={classes.head}>
        <div>
          <h2 className={classes.title}>{t(`fuel.transfers.titles.${kind}`)}</h2>
          <p className={classes.subtitle}>{t(`fuel.transfers.subtitles.${kind}`)}</p>
        </div>
        <div className={classes.picker}>
          {detail ? <StatusBadge status={detail.status} /> : null}
          <RecordPicker
            records={ledger.records}
            selectedId={ledger.selectedId}
            onSelect={(id) => ledger.select(id)}
            onNew={() => ledger.select(null)}
            canCreate={editorSide && (context?.can.create ?? false)}
          />
        </div>
      </div>
      <div className={classes.body}>
        {editorSide ? null : <OfficeNote />}
        <StatusBanner detail={detail} people={ledger.people} correctsNo={correctsNo} />
        <Notices error={ledger.error} done={ledger.done} />
        <fieldset className={classes.kinds} aria-label={t('fuel.transfers.kindsLabel')}>
          {KINDS.map((entry) => (
            <button
              key={entry.kind}
              type="button"
              className={classes.kind}
              aria-pressed={kind === entry.kind}
              disabled={!editable && kind !== entry.kind}
              onClick={() => set('kind', entry.kind)}
            >
              <span aria-hidden>{entry.icon}</span>
              {t(`fuel.transfers.kinds.${entry.kind}`)}
            </button>
          ))}
        </fieldset>
        <div className={classes.grid3wide}>{fields}</div>

        {kind === 'transfer' ? (
          <>
            <div className={classes.diagram}>
              {tankFigure(source1, out)}
              <div className={classes.arrow}>
                <span className={classes.arrowValue}>{mass ? `${mt(mass)} MT` : '— MT'}</span>
                <span className={classes.arrowLine} aria-hidden />
              </div>
              {tankFigure(target, into)}
            </div>
            <h3 className={classes.checkTitle}>{t('fuel.transfers.check')}</h3>
            <div
              className={classes.tableWrap}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>{t('fuel.transfers.checkColumns.tank')}</th>
                    <th className={classes.center}>{t('fuel.transfers.checkColumns.before')}</th>
                    <th className={classes.center}>{t('fuel.transfers.checkColumns.after')}</th>
                    <th className={classes.center}>{t('fuel.transfers.checkColumns.diff')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[out, into].map((row, index) =>
                    row ? (
                      <tr key={row.tankId}>
                        <td>
                          <strong>
                            {index === 0
                              ? t('fuel.transfers.sourceRow', { name: tankName(row.tankId) })
                              : t('fuel.transfers.targetRow', { name: tankName(row.tankId) })}
                          </strong>
                        </td>
                        <td className={`${classes.center} ${classes.num}`}>{mt(row.before)}</td>
                        <td className={`${classes.center} ${classes.num}`}>{mt(row.after)}</td>
                        <td className={`${classes.center} ${classes.num}`}>
                          {diffText(row.before, row.after)}
                        </td>
                      </tr>
                    ) : null,
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {(detail?.warnings ?? []).length > 0 ? (
          <Alert color="orange" role="note" className={classes.banner}>
            {(detail?.warnings ?? []).map((w) => (
              <div key={`${w.code}-${w.tankId}`}>
                {t(`fuel.transfers.warnings.${w.code}`, { tank: tankName(w.tankId) })}
              </div>
            ))}
          </Alert>
        ) : null}

        <div className={classes.bottom}>
          <div>
            <Textarea
              label={t('fuel.transfers.fields.description')}
              autosize
              minRows={3}
              maxLength={DESCRIPTION_MAX}
              readOnly={!editable}
              value={values.description}
              onChange={(e) => set('description', e.currentTarget.value)}
            />
            <div className={classes.counter}>
              {values.description.length}/{DESCRIPTION_MAX}
            </div>
          </div>
          <div>
            <span className={classes.fileLabel}>{t('fuel.transfers.fields.documents')}</span>
            {files.map((file) => (
              <div className={classes.file} key={file.id} style={{ marginBottom: 8 }}>
                <IconFileText size={22} className={classes.fileIcon} aria-hidden />
                <span className={classes.fileText}>
                  <span className={classes.fileName}>{file.fileName}</span>
                </span>
                {editable ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={t('fuel.file.remove', { name: file.fileName })}
                    onClick={() => ledger.setRemoved((prev) => [...prev, file.id])}
                  >
                    <IconX size={18} />
                  </ActionIcon>
                ) : (
                  <span />
                )}
              </div>
            ))}
            {pending ? (
              <div className={classes.file} style={{ marginBottom: 8 }}>
                <IconFileText size={22} className={classes.fileIcon} aria-hidden />
                <span className={classes.fileText}>
                  <span className={classes.fileName}>{pending.name}</span>
                </span>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={t('fuel.file.remove', { name: pending.name })}
                  onClick={() => ledger.setPendingFiles({})}
                >
                  <IconX size={18} />
                </ActionIcon>
              </div>
            ) : null}
            <div className={classes.drop}>
              <IconPaperclip size={22} aria-hidden />
              <span>
                {editorSide && (editable || status === 'approved') ? (
                  <FileButton
                    accept="application/pdf,image/jpeg,image/png"
                    onChange={(file) => {
                      if (file) ledger.setPendingFiles({ other: file });
                    }}
                  >
                    {(props) => (
                      <button type="button" className={classes.dropLink} {...props}>
                        {t('fuel.file.choose')}
                      </button>
                    )}
                  </FileButton>
                ) : (
                  <strong>{t('fuel.file.none')}</strong>
                )}{' '}
                <br />
                {t('fuel.file.types')}
              </span>
            </div>
          </div>
          <div className={classes.note} role="note">
            <IconInfoCircle size={20} style={{ color: 'var(--felix-info-fg)' }} aria-hidden />
            <span>
              <strong className={classes.noteTitle}>
                {t(`fuel.transfers.notes.${kind}.title`)}
              </strong>
              {t(`fuel.transfers.notes.${kind}.body`)}
            </span>
          </div>
        </div>
      </div>
      {actions.length > 0 ? (
        <div className={classes.footer}>
          <WorkflowButtons
            actions={actions}
            busy={ledger.busy}
            onAction={(action) => {
              if (action === 'save') void ledger.save(input());
              else if (action === 'submit') void ledger.submit(input());
              else if (action === 'correct') void ledger.correct();
              else setModal(action);
            }}
          />
        </div>
      ) : null}
      <DecisionModal
        mode={modal}
        recordNo={detail?.recordNo ?? ''}
        onClose={() => setModal(null)}
        onConfirm={(decision) => (modal ? ledger.decide(modal, decision) : Promise.resolve())}
      />
    </section>
  );
}
